/**
 * /compensation — Compensation Management (Section 4A).
 *
 * HR_ADMIN+ gated. Three tabs:
 *   - Overview: KPI cards (avg/median/total payroll), band distribution,
 *     per-department breakdown.
 *   - Bands: editable salary ranges by title (± department).
 *   - People: per-employee table with band-position bars and a quick
 *     "Record change" slide-over.
 *   - Equity: light pay-equity view comparing group averages vs overall.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import {
  AlertTriangle,
  BarChart3,
  DollarSign,
  Pencil,
  Plus,
  TrendingUp,
  Users,
} from "lucide-react";

import { pageTransition } from "@/lib/utils";
import { useToast } from "@/hooks/useToast";

import {
  createBand,
  fetchEquity,
  fetchOverview,
  listBands,
  listEmployeeComp,
  recordChange,
  updateBand,
  type BandStatus,
  type CompChangeType,
  type CompPeriod,
  type CompensationBand,
  type CompensationOverview,
  type EmployeeCompRow,
  type EquityReport,
} from "@/api/compensation";
import { listDepartments } from "@/api/organizations";
import type { DepartmentFull } from "@/types/organization";

import { PageHeader } from "@/components/layout/PageHeader";
import { Card } from "@/components/ui/Card";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { Select } from "@/components/ui/Select";
import { Badge } from "@/components/ui/Badge";
import { Modal } from "@/components/ui/Modal";
import { Tabs } from "@/components/ui/Tabs";

type TabId = "overview" | "bands" | "people" | "equity";

const CHANGE_TYPES: { value: CompChangeType; label: string }[] = [
  { value: "merit_increase", label: "Merit increase" },
  { value: "promotion", label: "Promotion" },
  { value: "adjustment", label: "Adjustment" },
  { value: "cost_of_living", label: "Cost of living" },
  { value: "bonus", label: "Bonus" },
  { value: "hire", label: "New hire" },
  { value: "demotion", label: "Demotion" },
];

function formatCurrency(value: number | null | undefined, currency = "USD"): string {
  if (value == null) return "—";
  return new Intl.NumberFormat(undefined, {
    style: "currency",
    currency,
    maximumFractionDigits: 0,
  }).format(value);
}

type BadgeVariant = "default" | "success" | "warning" | "danger" | "info";

function bandBadgeVariant(status: BandStatus): BadgeVariant {
  switch (status) {
    case "within":
      return "success";
    case "above":
      return "warning";
    case "below":
      return "danger";
    default:
      return "default";
  }
}

export function CompensationPage() {
  const { addToast } = useToast();
  const [tab, setTab] = useState<TabId>("overview");

  const [overview, setOverview] = useState<CompensationOverview | null>(null);
  const [bands, setBands] = useState<CompensationBand[]>([]);
  const [people, setPeople] = useState<EmployeeCompRow[]>([]);
  const [equity, setEquity] = useState<EquityReport | null>(null);
  const [departments, setDepartments] = useState<DepartmentFull[]>([]);
  const [loading, setLoading] = useState(true);

  // Filters on "people" tab
  const [filterDept, setFilterDept] = useState<string>("");
  const [filterBandStatus, setFilterBandStatus] = useState<string>("");
  const [search, setSearch] = useState<string>("");

  // Modals
  const [bandModalOpen, setBandModalOpen] = useState(false);
  const [editingBand, setEditingBand] = useState<CompensationBand | null>(null);
  const [changeModalOpen, setChangeModalOpen] = useState(false);
  const [changeTarget, setChangeTarget] = useState<EmployeeCompRow | null>(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const [ov, bd, pl, eq, dp] = await Promise.all([
        fetchOverview(),
        listBands(true),
        listEmployeeComp(),
        fetchEquity(),
        listDepartments().catch(() => [] as DepartmentFull[]),
      ]);
      setOverview(ov);
      setBands(bd);
      setPeople(pl);
      setEquity(eq);
      setDepartments(dp);
    } catch (e: any) {
      addToast(
        "error",
        `Couldn't load compensation data: ${e?.response?.data?.detail ?? "Try again."}`,
      );
    } finally {
      setLoading(false);
    }
  }, [addToast]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  // Re-fetch people with filters
  const refreshPeople = useCallback(async () => {
    try {
      const rows = await listEmployeeComp({
        department_id: filterDept || undefined,
        band_status: (filterBandStatus || undefined) as BandStatus | undefined,
        search: search || undefined,
      });
      setPeople(rows);
    } catch (e: any) {
      addToast("error", "Filter failed");
    }
  }, [filterDept, filterBandStatus, search, addToast]);

  useEffect(() => {
    if (tab === "people") refreshPeople();
  }, [tab, refreshPeople]);

  const deptOptions = useMemo(
    () => [
      { value: "", label: "All departments" },
      ...departments.map((d) => ({ value: d.id, label: d.name })),
    ],
    [departments],
  );

  return (
    <motion.div {...pageTransition}>
      <PageHeader
        title="Compensation"
        subtitle="Salary bands, pay distribution, and equity analytics"
      />

      <Tabs
        tabs={[
          { id: "overview", label: "Overview" },
          { id: "bands", label: "Bands" },
          { id: "people", label: "People" },
          { id: "equity", label: "Equity" },
        ]}
        activeTab={tab}
        onChange={(id) => setTab(id as TabId)}
      />

      <div className="mt-6">
        {tab === "overview" && (
          <OverviewTab overview={overview} loading={loading} />
        )}
        {tab === "bands" && (
          <BandsTab
            bands={bands}
            departments={departments}
            loading={loading}
            onEdit={(b) => {
              setEditingBand(b);
              setBandModalOpen(true);
            }}
            onNew={() => {
              setEditingBand(null);
              setBandModalOpen(true);
            }}
          />
        )}
        {tab === "people" && (
          <PeopleTab
            rows={people}
            loading={loading}
            deptOptions={deptOptions}
            filterDept={filterDept}
            setFilterDept={setFilterDept}
            filterBandStatus={filterBandStatus}
            setFilterBandStatus={setFilterBandStatus}
            search={search}
            setSearch={setSearch}
            onRecordChange={(row) => {
              setChangeTarget(row);
              setChangeModalOpen(true);
            }}
          />
        )}
        {tab === "equity" && <EquityTab report={equity} loading={loading} />}
      </div>

      {bandModalOpen && (
        <BandModal
          band={editingBand}
          departments={departments}
          onClose={() => setBandModalOpen(false)}
          onSaved={async () => {
            setBandModalOpen(false);
            await refresh();
            addToast("success", "Band saved");
          }}
        />
      )}

      {changeModalOpen && changeTarget && (
        <ChangeModal
          target={changeTarget}
          onClose={() => setChangeModalOpen(false)}
          onSaved={async () => {
            setChangeModalOpen(false);
            await refresh();
            addToast("success", "Change recorded");
          }}
        />
      )}
    </motion.div>
  );
}

// ─────────────────────────── Overview tab ───────────────────────────

function OverviewTab({
  overview,
  loading,
}: {
  overview: CompensationOverview | null;
  loading: boolean;
}) {
  if (loading || !overview) {
    return <Card className="p-6 text-sm text-text-muted">Loading overview…</Card>;
  }
  const total =
    overview.below_band_count +
    overview.within_band_count +
    overview.above_band_count +
    overview.no_band_count;

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <KpiCard
          label="Active employees"
          value={overview.employee_count.toString()}
          icon={<Users className="h-5 w-5" />}
        />
        <KpiCard
          label="Total payroll (yearly)"
          value={formatCurrency(overview.total_payroll, overview.currency)}
          icon={<DollarSign className="h-5 w-5" />}
        />
        <KpiCard
          label="Average salary"
          value={formatCurrency(overview.avg_salary, overview.currency)}
          icon={<TrendingUp className="h-5 w-5" />}
        />
        <KpiCard
          label="Median salary"
          value={formatCurrency(overview.median_salary, overview.currency)}
          icon={<BarChart3 className="h-5 w-5" />}
        />
      </div>

      <Card className="p-5">
        <h3 className="text-sm font-semibold mb-4">Band distribution</h3>
        <div className="flex h-3 w-full overflow-hidden rounded-full bg-surface-muted">
          {total > 0 && (
            <>
              <div
                className="bg-danger"
                style={{ width: `${(overview.below_band_count / total) * 100}%` }}
                title={`Below band: ${overview.below_band_count}`}
              />
              <div
                className="bg-success"
                style={{ width: `${(overview.within_band_count / total) * 100}%` }}
                title={`Within band: ${overview.within_band_count}`}
              />
              <div
                className="bg-warning"
                style={{ width: `${(overview.above_band_count / total) * 100}%` }}
                title={`Above band: ${overview.above_band_count}`}
              />
              <div
                className="bg-border"
                style={{ width: `${(overview.no_band_count / total) * 100}%` }}
                title={`No band: ${overview.no_band_count}`}
              />
            </>
          )}
        </div>
        <div className="mt-3 flex flex-wrap gap-4 text-xs text-text-muted">
          <LegendDot color="bg-danger" label={`Below (${overview.below_band_count})`} />
          <LegendDot color="bg-success" label={`Within (${overview.within_band_count})`} />
          <LegendDot color="bg-warning" label={`Above (${overview.above_band_count})`} />
          <LegendDot color="bg-border" label={`No band (${overview.no_band_count})`} />
        </div>
      </Card>

      <Card className="p-5">
        <h3 className="text-sm font-semibold mb-4">By department</h3>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-left text-xs text-text-muted">
              <tr>
                <th className="py-2 pr-4 font-medium">Department</th>
                <th className="py-2 pr-4 font-medium">Headcount</th>
                <th className="py-2 pr-4 font-medium">Avg salary</th>
                <th className="py-2 pr-4 font-medium">Median</th>
                <th className="py-2 font-medium">Total payroll</th>
              </tr>
            </thead>
            <tbody>
              {overview.by_department.length === 0 && (
                <tr>
                  <td
                    colSpan={5}
                    className="py-4 text-center text-xs text-text-muted"
                  >
                    No department data yet.
                  </td>
                </tr>
              )}
              {overview.by_department.map((d) => (
                <tr key={d.department_id ?? "none"} className="border-t border-border">
                  <td className="py-2 pr-4">{d.department_name ?? "—"}</td>
                  <td className="py-2 pr-4">{d.employee_count}</td>
                  <td className="py-2 pr-4">
                    {formatCurrency(d.avg_salary, overview.currency)}
                  </td>
                  <td className="py-2 pr-4">
                    {formatCurrency(d.median_salary, overview.currency)}
                  </td>
                  <td className="py-2">
                    {formatCurrency(d.total_payroll, overview.currency)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}

function KpiCard({
  label,
  value,
  icon,
}: {
  label: string;
  value: string;
  icon: React.ReactNode;
}) {
  return (
    <Card className="p-5">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-xs text-text-muted">{label}</p>
          <p className="mt-1 text-xl font-semibold text-text-primary">{value}</p>
        </div>
        <div className="text-accent">{icon}</div>
      </div>
    </Card>
  );
}

function LegendDot({ color, label }: { color: string; label: string }) {
  return (
    <span className="inline-flex items-center gap-2">
      <span className={`h-2 w-2 rounded-full ${color}`} />
      {label}
    </span>
  );
}

// ─────────────────────────── Bands tab ───────────────────────────

function BandsTab({
  bands,
  departments,
  loading,
  onEdit,
  onNew,
}: {
  bands: CompensationBand[];
  departments: DepartmentFull[];
  loading: boolean;
  onEdit: (b: CompensationBand) => void;
  onNew: () => void;
}) {
  const deptName = (id: string | null) =>
    id ? departments.find((d) => d.id === id)?.name ?? "—" : "All departments";

  return (
    <Card className="p-5">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-sm font-semibold">Salary bands</h3>
        <Button size="sm" onClick={onNew}>
          <Plus className="h-4 w-4" /> New band
        </Button>
      </div>
      {loading ? (
        <p className="text-sm text-text-muted">Loading…</p>
      ) : bands.length === 0 ? (
        <p className="text-sm text-text-muted">
          No bands yet. Create one to anchor target salary ranges by title.
        </p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-left text-xs text-text-muted">
              <tr>
                <th className="py-2 pr-4 font-medium">Title</th>
                <th className="py-2 pr-4 font-medium">Department</th>
                <th className="py-2 pr-4 font-medium">Min</th>
                <th className="py-2 pr-4 font-medium">Mid</th>
                <th className="py-2 pr-4 font-medium">Max</th>
                <th className="py-2 pr-4 font-medium">Active</th>
                <th className="py-2 font-medium"></th>
              </tr>
            </thead>
            <tbody>
              {bands.map((b) => (
                <tr key={b.id} className="border-t border-border">
                  <td className="py-2 pr-4 font-medium">{b.title}</td>
                  <td className="py-2 pr-4 text-text-muted">
                    {deptName(b.department_id)}
                  </td>
                  <td className="py-2 pr-4">
                    {formatCurrency(b.min_salary, b.currency)}
                  </td>
                  <td className="py-2 pr-4">
                    {formatCurrency(b.mid_salary, b.currency)}
                  </td>
                  <td className="py-2 pr-4">
                    {formatCurrency(b.max_salary, b.currency)}
                  </td>
                  <td className="py-2 pr-4">
                    <Badge variant={b.is_active ? "success" : "default"}>
                      {b.is_active ? "Active" : "Inactive"}
                    </Badge>
                  </td>
                  <td className="py-2 text-right">
                    <Button size="sm" variant="ghost" onClick={() => onEdit(b)}>
                      <Pencil className="h-3.5 w-3.5" />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
}

// ─────────────────────────── People tab ───────────────────────────

function PeopleTab({
  rows,
  loading,
  deptOptions,
  filterDept,
  setFilterDept,
  filterBandStatus,
  setFilterBandStatus,
  search,
  setSearch,
  onRecordChange,
}: {
  rows: EmployeeCompRow[];
  loading: boolean;
  deptOptions: { value: string; label: string }[];
  filterDept: string;
  setFilterDept: (v: string) => void;
  filterBandStatus: string;
  setFilterBandStatus: (v: string) => void;
  search: string;
  setSearch: (v: string) => void;
  onRecordChange: (r: EmployeeCompRow) => void;
}) {
  return (
    <Card className="p-5">
      <div className="flex flex-col md:flex-row gap-3 mb-4">
        <Input
          placeholder="Search name or title…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="md:w-64"
        />
        <Select
          value={filterDept}
          onChange={(e) => setFilterDept(e.target.value)}
          options={deptOptions}
          className="md:w-56"
        />
        <Select
          value={filterBandStatus}
          onChange={(e) => setFilterBandStatus(e.target.value)}
          options={[
            { value: "", label: "All band positions" },
            { value: "below", label: "Below band" },
            { value: "within", label: "Within band" },
            { value: "above", label: "Above band" },
            { value: "no_band", label: "No band" },
          ]}
          className="md:w-56"
        />
      </div>

      {loading ? (
        <p className="text-sm text-text-muted">Loading…</p>
      ) : rows.length === 0 ? (
        <p className="text-sm text-text-muted">No employees match these filters.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-left text-xs text-text-muted">
              <tr>
                <th className="py-2 pr-4 font-medium">Employee</th>
                <th className="py-2 pr-4 font-medium">Title</th>
                <th className="py-2 pr-4 font-medium">Department</th>
                <th className="py-2 pr-4 font-medium">Current</th>
                <th className="py-2 pr-4 font-medium w-48">Band position</th>
                <th className="py-2 pr-4 font-medium">Status</th>
                <th className="py-2 font-medium"></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.employee_id} className="border-t border-border">
                  <td className="py-2 pr-4 font-medium">
                    {r.first_name} {r.last_name}
                  </td>
                  <td className="py-2 pr-4 text-text-muted">{r.title}</td>
                  <td className="py-2 pr-4 text-text-muted">
                    {r.department_name ?? "—"}
                  </td>
                  <td className="py-2 pr-4">
                    {formatCurrency(r.current_amount, r.currency)}
                    <span className="text-xs text-text-muted"> /{r.period}</span>
                  </td>
                  <td className="py-2 pr-4 w-48">
                    <BandBar row={r} />
                  </td>
                  <td className="py-2 pr-4">
                    <Badge variant={bandBadgeVariant(r.band_status)}>
                      {r.band_status}
                    </Badge>
                  </td>
                  <td className="py-2 text-right">
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => onRecordChange(r)}
                    >
                      Record change
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
}

function BandBar({ row }: { row: EmployeeCompRow }) {
  if (row.band_min == null || row.band_max == null || row.band_position == null) {
    return <span className="text-xs text-text-muted">No band</span>;
  }
  // Clamp visual position to [-0.15, 1.15] so below/above still visible
  const clamped = Math.max(-0.15, Math.min(1.15, row.band_position));
  const left = `${((clamped + 0.15) / 1.3) * 100}%`;
  return (
    <div className="relative h-2 w-full rounded-full bg-surface-muted">
      <div className="absolute inset-y-0 left-[11.5%] right-[11.5%] rounded-full bg-accent/20" />
      <div
        className="absolute top-1/2 -translate-y-1/2 h-3 w-3 rounded-full bg-accent border-2 border-surface"
        style={{ left }}
        title={`${formatCurrency(row.current_amount, row.currency)} in band ${formatCurrency(
          row.band_min,
          row.currency,
        )} – ${formatCurrency(row.band_max, row.currency)}`}
      />
    </div>
  );
}

// ─────────────────────────── Equity tab ───────────────────────────

function EquityTab({
  report,
  loading,
}: {
  report: EquityReport | null;
  loading: boolean;
}) {
  if (loading || !report) {
    return <Card className="p-6 text-sm text-text-muted">Loading equity report…</Card>;
  }
  const sections: { title: string; rows: typeof report.by_department }[] = [
    { title: "By department", rows: report.by_department },
    { title: "By tenure", rows: report.by_tenure },
    { title: "By employment type", rows: report.by_employment_type },
  ];
  return (
    <div className="space-y-6">
      <Card className="p-5">
        <h3 className="text-sm font-semibold mb-3">Baseline</h3>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 text-sm">
          <div>
            <p className="text-xs text-text-muted">Overall average</p>
            <p className="text-lg font-semibold">
              {formatCurrency(report.overall_avg, report.currency)}
            </p>
          </div>
          <div>
            <p className="text-xs text-text-muted">Overall median</p>
            <p className="text-lg font-semibold">
              {formatCurrency(report.overall_median, report.currency)}
            </p>
          </div>
          <div>
            <p className="text-xs text-text-muted">Flag threshold</p>
            <p className="text-lg font-semibold">
              ±{Math.round(report.flag_threshold * 100)}%
            </p>
          </div>
        </div>
      </Card>
      {sections.map((s) => (
        <Card key={s.title} className="p-5">
          <h3 className="text-sm font-semibold mb-3">{s.title}</h3>
          {s.rows.length === 0 ? (
            <p className="text-sm text-text-muted">No data.</p>
          ) : (
            <table className="w-full text-sm">
              <thead className="text-left text-xs text-text-muted">
                <tr>
                  <th className="py-2 pr-4 font-medium">Group</th>
                  <th className="py-2 pr-4 font-medium">Headcount</th>
                  <th className="py-2 pr-4 font-medium">Avg</th>
                  <th className="py-2 pr-4 font-medium">Median</th>
                  <th className="py-2 pr-4 font-medium">vs overall</th>
                  <th className="py-2 font-medium"></th>
                </tr>
              </thead>
              <tbody>
                {s.rows.map((row) => (
                  <tr key={row.key} className="border-t border-border">
                    <td className="py-2 pr-4">{row.label}</td>
                    <td className="py-2 pr-4">{row.employee_count}</td>
                    <td className="py-2 pr-4">
                      {formatCurrency(row.avg_salary, report.currency)}
                    </td>
                    <td className="py-2 pr-4">
                      {formatCurrency(row.median_salary, report.currency)}
                    </td>
                    <td className="py-2 pr-4">
                      <span
                        className={
                          row.ratio_vs_overall < 1
                            ? "text-danger"
                            : row.ratio_vs_overall > 1
                              ? "text-success"
                              : ""
                        }
                      >
                        {Math.round(row.ratio_vs_overall * 100)}%
                      </span>
                    </td>
                    <td className="py-2">
                      {row.flagged && (
                        <Badge variant="warning">
                          <AlertTriangle className="h-3 w-3 mr-1" />
                          Flagged
                        </Badge>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Card>
      ))}
    </div>
  );
}

// ─────────────────────────── Band modal ───────────────────────────

function BandModal({
  band,
  departments,
  onClose,
  onSaved,
}: {
  band: CompensationBand | null;
  departments: DepartmentFull[];
  onClose: () => void;
  onSaved: () => void;
}) {
  const { addToast } = useToast();
  const [title, setTitle] = useState(band?.title ?? "");
  const [departmentId, setDepartmentId] = useState<string>(
    band?.department_id ?? "",
  );
  const [minSalary, setMinSalary] = useState(band?.min_salary?.toString() ?? "");
  const [midSalary, setMidSalary] = useState(band?.mid_salary?.toString() ?? "");
  const [maxSalary, setMaxSalary] = useState(band?.max_salary?.toString() ?? "");
  const [currency, setCurrency] = useState(band?.currency ?? "USD");
  const [isActive, setIsActive] = useState(band?.is_active ?? true);
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!title.trim()) {
      addToast("error", "Title is required");
      return;
    }
    const mn = parseFloat(minSalary);
    const md = parseFloat(midSalary);
    const mx = parseFloat(maxSalary);
    if (Number.isNaN(mn) || Number.isNaN(md) || Number.isNaN(mx)) {
      addToast("error", "Min / Mid / Max must all be numbers");
      return;
    }
    if (!(mn <= md && md <= mx)) {
      addToast("error", "Expected min ≤ mid ≤ max");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        title: title.trim(),
        department_id: departmentId || null,
        min_salary: mn,
        mid_salary: md,
        max_salary: mx,
        currency,
        is_active: isActive,
      };
      if (band) {
        await updateBand(band.id, payload);
      } else {
        await createBand(payload);
      }
      onSaved();
    } catch (e: any) {
      addToast(
        "error",
        `Save failed: ${e?.response?.data?.detail ?? "Try again."}`,
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      isOpen
      onClose={onClose}
      title={band ? "Edit band" : "New band"}
      size="md"
    >
      <div className="space-y-4">
        <Input
          label="Title"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="e.g. Senior Engineer"
        />
        <Select
          label="Department"
          value={departmentId}
          onChange={(e) => setDepartmentId(e.target.value)}
          options={[
            { value: "", label: "All departments (fallback)" },
            ...departments.map((d) => ({ value: d.id, label: d.name })),
          ]}
        />
        <div className="grid grid-cols-3 gap-3">
          <Input
            label="Min"
            type="number"
            value={minSalary}
            onChange={(e) => setMinSalary(e.target.value)}
          />
          <Input
            label="Mid"
            type="number"
            value={midSalary}
            onChange={(e) => setMidSalary(e.target.value)}
          />
          <Input
            label="Max"
            type="number"
            value={maxSalary}
            onChange={(e) => setMaxSalary(e.target.value)}
          />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Input
            label="Currency"
            value={currency}
            onChange={(e) => setCurrency(e.target.value.toUpperCase())}
            maxLength={3}
          />
          <label className="flex items-center gap-2 text-sm mt-6">
            <input
              type="checkbox"
              checked={isActive}
              onChange={(e) => setIsActive(e.target.checked)}
            />
            Active
          </label>
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={save} disabled={saving}>
            {saving ? "Saving…" : "Save band"}
          </Button>
        </div>
      </div>
    </Modal>
  );
}

// ─────────────────────────── Change modal ───────────────────────────

function ChangeModal({
  target,
  onClose,
  onSaved,
}: {
  target: EmployeeCompRow;
  onClose: () => void;
  onSaved: () => void;
}) {
  const { addToast } = useToast();
  const [effectiveDate, setEffectiveDate] = useState(
    new Date().toISOString().slice(0, 10),
  );
  const [amount, setAmount] = useState(target.current_amount?.toString() ?? "");
  const [period, setPeriod] = useState<CompPeriod>(target.period);
  const [changeType, setChangeType] = useState<CompChangeType>("merit_increase");
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);

  const save = async () => {
    const amt = parseFloat(amount);
    if (Number.isNaN(amt) || amt < 0) {
      addToast("error", "Amount must be a positive number");
      return;
    }
    setSaving(true);
    try {
      await recordChange({
        employee_id: target.employee_id,
        effective_date: effectiveDate,
        amount: amt,
        currency: target.currency,
        period,
        change_type: changeType,
        reason: reason.trim() || null,
        title_at_time: target.title,
      });
      onSaved();
    } catch (e: any) {
      addToast(
        "error",
        `Save failed: ${e?.response?.data?.detail ?? "Try again."}`,
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      isOpen
      onClose={onClose}
      title={`Record compensation change — ${target.first_name} ${target.last_name}`}
      size="md"
    >
      <div className="space-y-4">
        <div className="text-xs text-text-muted">
          Current: {formatCurrency(target.current_amount, target.currency)} /
          {target.period}
        </div>
        <Input
          label="Effective date"
          type="date"
          value={effectiveDate}
          onChange={(e) => setEffectiveDate(e.target.value)}
        />
        <div className="grid grid-cols-2 gap-3">
          <Input
            label="New amount"
            type="number"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
          />
          <Select
            label="Period"
            value={period}
            onChange={(e) => setPeriod(e.target.value as CompPeriod)}
            options={[
              { value: "yearly", label: "Yearly" },
              { value: "monthly", label: "Monthly" },
              { value: "hourly", label: "Hourly" },
            ]}
          />
        </div>
        <Select
          label="Change type"
          value={changeType}
          onChange={(e) => setChangeType(e.target.value as CompChangeType)}
          options={CHANGE_TYPES.map((c) => ({ value: c.value, label: c.label }))}
        />
        <div>
          <label className="block text-sm font-medium mb-1">Reason</label>
          <textarea
            className="w-full rounded-lg border border-border bg-surface px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-accent/50"
            rows={3}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Optional context for the change"
          />
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={save} disabled={saving}>
            {saving ? "Saving…" : "Record change"}
          </Button>
        </div>
      </div>
    </Modal>
  );
}

export default CompensationPage;
